/**
 * Past-approvals hook (Automations v2 §5) — powers the Notifications "Past" tab.
 * A read-only HISTORY of what the automations did and you decided on: every `agent_approvals`
 * row in a terminal, human-decided state (approved | edited | executed | rejected), newest-first.
 * `toPastApproval` is the pure raw→display mapping (exported for unit tests).
 */
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import { useSession } from '@/features/auth/hooks/useSession';
import { resolveWorkspaceId } from '@/lib/workspace-routing';
import * as api from '@/lib/api/endpoints/agent-trust';
import type { AgentApproval, AgentApprovalStatus } from '@/lib/api/endpoints/agent-trust';
import {
  actionObjectType,
  actionOperation,
  type ActionOperation,
} from '@/common/utils/action-vocabulary';

export type PastOutcome =
  | 'sent'
  | 'edited_sent'
  | 'created'
  | 'updated'
  | 'deleted'
  | 'discarded'
  | 'done';

const OUTCOME_LABEL: Record<PastOutcome, string> = {
  sent: 'Sent',
  edited_sent: 'Edited & sent',
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
  discarded: 'Discarded',
  done: 'Done',
};

export interface PastApproval {
  id: string;
  outcome: PastOutcome;
  label: string;
  operation: ActionOperation;
  objectType: string | null;
  title: string;
  decidedAt: string;
}

function pastOutcome(status: AgentApprovalStatus, operation: ActionOperation): PastOutcome {
  if (status === 'rejected') return 'discarded';
  if (operation === 'send') return status === 'edited' ? 'edited_sent' : 'sent';
  if (operation === 'create') return 'created';
  if (operation === 'update') return 'updated';
  if (operation === 'delete') return 'deleted';
  return 'done';
}

function str(v: unknown): string {
  return typeof v === 'string' && v.trim() ? v : '';
}

/** Flatten one raw decided `AgentApproval` into the render-ready `PastApproval`. Pure. */
export function toPastApproval(a: AgentApproval): PastApproval {
  const payload = a.preparedPayload ?? {};
  const operation = actionOperation(a.actionType);
  const outcome = pastOutcome(a.status, operation);
  const title =
    str(payload.subject) || str(payload.targetLabel) || str(payload.title) || a.actionType;
  return {
    id: a.id,
    outcome,
    label: OUTCOME_LABEL[outcome],
    operation,
    objectType: actionObjectType(a.actionType),
    title,
    decidedAt: a.decidedAt ?? a.createdAt,
  };
}

/**
 * The decided approvals for the active workspace, newest-first, mapped to compact history rows.
 * `enabled` gates the fetch (pass `tab === 'past'` so it only loads when Past is open).
 */
export function usePastApprovals(enabled = true) {
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const q = useQuery({
    queryKey: queryKeys.agentTrust.decidedApprovals(workspaceId),
    queryFn: () => api.listDecidedApprovals(token!, workspaceId),
    enabled: enabled && !!token && !!workspaceId,
    staleTime: 30_000,
  });
  const items = (q.data?.items ?? []).map(toPastApproval);
  return { items, isLoading: q.isLoading, error: q.error, refetch: q.refetch };
}
